/**
 * Article Autocomplete Hooks
 * 
 * Provides autocomplete functionality for article search using Elasticsearch.
 * Two hooks available:
 * - useArticleAutocomplete: For TextInput components (syncs with external value)
 * - useArticlesAutocomplete: For select-search fields in forms
 */

import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useDebouncedValue } from '../../../hooks/useDebouncedValue'
import { v1Api } from '../../../api/generatedClient'
import { getApiBaseUrl } from '../../../api/authTokens'
import type { APIAutocompleteResponse } from '../../../generated-types/models/APIAutocompleteResponse'

interface ArticleAutocompleteResponse {
  suggestions: Array<{
    id: string
    name: string
    number?: string
    self: string
  }>
  total: number
}

/**
 * Fetches article autocomplete suggestions
 */
const fetchArticleAutocomplete = async (
  query: string,
  size = 10
): Promise<ArticleAutocompleteResponse> => {
  const response: APIAutocompleteResponse = await v1Api.v1SearchItemsArticlesAutocompleteRetrieve({
    q: query || undefined,
    size,
  })

  const baseUrl = getApiBaseUrl()

  return {
    suggestions: response.suggestions.map(suggestion => ({
      id: suggestion.id,
      name: suggestion.name,
      number: suggestion.number,
      self: `${baseUrl}v1/items/articles/${suggestion.id}/`,
    })),
    total: response.total,
  }
}

/**
 * Original hook for ArticleAutocompleteInput component
 * Syncs with external value and uses Elasticsearch autocomplete
 * 
 * @param externalValue - The current value from parent component
 * @param options - Configuration options
 * @returns Hook with inputTerm, debouncedTerm, suggestions, isSearching, error
 */
export function useArticleAutocomplete(
  externalValue: string,
  options: {
    size?: number
    minLength?: number
    debounceMs?: number
    enabled?: boolean
  } = {}
) {
  const { size = 10, minLength = 2, debounceMs = 300, enabled = true } = options
  const [inputTerm, setInputTerm] = useState(externalValue)
  
  // Sync with external value changes
  useEffect(() => {
    setInputTerm(externalValue)
  }, [externalValue])
  
  const debouncedTerm = useDebouncedValue(inputTerm, debounceMs)

  const { data, isLoading, error } = useQuery<ArticleAutocompleteResponse, Error>({
    queryKey: ['article-search-autocomplete', debouncedTerm],
    queryFn: () => fetchArticleAutocomplete(debouncedTerm, size),
    enabled: enabled && debouncedTerm.length >= minLength,
    staleTime: 30000,
    retry: 1,
    refetchOnWindowFocus: false,
  })

  return {
    inputTerm,
    debouncedTerm,
    suggestions: data?.suggestions ?? [],
    isSearching: isLoading && debouncedTerm.length >= minLength,
    error,
    updateTerm: setInputTerm,  // Function to update the search term
  }
}

/**
 * Hook for articles list autocomplete (for select fields)
 * 
 * This hook is specifically for select-search fields in forms
 * 
 * @param options - Configuration options
 * @returns Autocomplete hook result with suggestions and loading state
 */
export function useArticlesAutocomplete(
  options: {
    size?: number
    enabled?: boolean
  } = {}
) {
  const { size = 10, enabled = true } = options
  const [query, setQuery] = useState('')
  
  const debouncedQuery = useDebouncedValue(query, 300)

  const { data, isLoading, error } = useQuery<ArticleAutocompleteResponse, Error>({
    queryKey: ['articles-list-autocomplete', debouncedQuery],
    queryFn: () => fetchArticleAutocomplete(debouncedQuery, size),
    enabled: enabled && (debouncedQuery.length >= 2 || debouncedQuery === ''),
    staleTime: 30000,
    retry: 1,
    refetchOnWindowFocus: false,
  })

  return {
    suggestions: data?.suggestions ?? [],
    total: data?.total ?? 0,
    isLoading: isLoading && (debouncedQuery.length >= 2 || debouncedQuery === ''),
    error,
    updateQuery: setQuery,
    query,
  }
}

